import axios from "axios";

const ZAMMAD_API_URL = process.env.REACT_APP_ZAMMAD_API_URL;

/**
 * Crear instancia de Axios para Zammad con Basic Auth
 * @param {string} userEmail - Email del usuario autenticado
 * @returns {AxiosInstance} Instancia configurada de Axios
 */
export const createZammadAxiosInstance = (userEmail) => {
  return axios.create({
    baseURL: ZAMMAD_API_URL,
    auth: {
      username: userEmail,
      password: userEmail
    },
    headers: {
      'Content-Type': 'application/json'
    }
  });
};

/**
 * Crear ticket en Zammad
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {Object} ticketData - Datos del ticket a crear
 * @returns {Promise} Respuesta de la API
 */
export const CREATE_TICKET_ZAMMAD = (zammadInstance, ticketData) => {
  return zammadInstance.post('/tickets', ticketData);
};

/**
 * Obtener tickets del usuario
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {Object} params - Parámetros de búsqueda
 * @returns {Promise} Respuesta de la API
 */
export const GET_TICKETS_ZAMMAD = (zammadInstance, params = {}) => {
  return zammadInstance.get('/tickets', { params });
};

/**
 * Buscar tickets por criterios
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {string} query - Query de búsqueda
 * @param {number} limit - Límite de resultados
 * @returns {Promise} Respuesta de la API
 */
export const SEARCH_TICKETS_ZAMMAD = (zammadInstance, query, limit = 100) => {
  return zammadInstance.get(`/tickets/search?query=${encodeURIComponent(query)}&limit=${limit}`);
};

/**
 * Obtener un ticket específico por ID
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {number} ticketId - ID del ticket
 * @returns {Promise} Respuesta de la API
 */
export const GET_TICKET_BY_ID_ZAMMAD = (zammadInstance, ticketId) => {
  return zammadInstance.get(`/tickets/${ticketId}`);
};

/**
 * Actualizar ticket
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {number} ticketId - ID del ticket
 * @param {Object} updateData - Datos a actualizar
 * @returns {Promise} Respuesta de la API
 */
export const UPDATE_TICKET_ZAMMAD = (zammadInstance, ticketId, updateData) => {
  return zammadInstance.put(`/tickets/${ticketId}`, updateData);
};

/**
 * Crear artículo (comentario) en un ticket
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {Object} articleData - Datos del artículo
 * @returns {Promise} Respuesta de la API
 */
export const CREATE_ARTICLE_ZAMMAD = (zammadInstance, articleData) => {
  return zammadInstance.post('/ticket_articles', articleData);
};

/**
 * Obtener artículos de un ticket
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {number} ticketId - ID del ticket
 * @returns {Promise} Respuesta de la API
 */
export const GET_ARTICLES_BY_TICKET_ZAMMAD = (zammadInstance, ticketId) => {
  return zammadInstance.get(`/ticket_articles/by_ticket/${ticketId}`);
};

/**
 * Obtener información del usuario actual
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @returns {Promise} Respuesta de la API
 */
export const GET_CURRENT_USER_ZAMMAD = (zammadInstance) => {
  return zammadInstance.get('/users/me');
};

/**
 * Obtener grupos disponibles
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @returns {Promise} Respuesta de la API
 */
export const GET_GROUPS_ZAMMAD = (zammadInstance) => {
  return zammadInstance.get('/groups');
};

/**
 * Obtener estadísticas de tickets de un usuario (open_ids, closed_ids y detalle en assets)
 * @param {AxiosInstance} zammadInstance - Instancia autenticada de Zammad
 * @param {number} userId - ID del usuario en Zammad
 * @returns {Promise} Respuesta de la API
 */
export const GET_TICKET_STATS_ZAMMAD = (zammadInstance, userId) => {
  return zammadInstance.post('/ticket_stats', { user_id: userId, organization_id: [] });
};

/**
 * Convertir archivo a base64
 * @param {File} file - Archivo a convertir
 * @returns {Promise<string>} String en base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remover el prefijo data:mime-type;base64,
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
  });
};

/**
 * Mapear estados de SharePoint a estados de Zammad
 * @param {string} estado - Estado en SharePoint
 * @returns {string} Estado en Zammad
 */
export const mapEstadoToZammad = (estado) => {
  const estadosMap = {
    'Nuevo': 'new',
    'En Proceso': 'open',
    'Finalizado': 'closed',
    'Pendiente': 'pending reminder'
  };
  return estadosMap[estado] || 'new';
};

/**
 * Mapear estados de Zammad a estados para display
 * @param {number} stateId - ID del estado en Zammad
 * @returns {string} Estado para mostrar
 */
export const mapZammadStateToDisplay = (stateId) => {
  const statesMap = {
    1: 'Nuevo',
    2: 'En Proceso',
    3: 'Pendiente',
    4: 'Cerrado',
    // 5: 'Cerrado',
    5: 'Fusionado',
    6: 'Cierre pendiente'
  };
  return statesMap[stateId] || 'Desconocido';
};

/**
 * Obtener grupo de Zammad desde tipo de solicitud
 * @param {Object} tipoSolicitud - Objeto con datos del tipo de solicitud
 * @returns {number} ID del grupo en Zammad
 */
export const getZammadGroupFromTipoSolicitud = (tipoSolicitud) => {
  return tipoSolicitud?.GrupoZammad || 2; // Default grupo 2
};

/**
 * Función helper para validar que un objeto esté declarado y tenga propiedades
 * @param {Object} obj - Objeto a validar
 * @returns {boolean} True si el objeto tiene propiedades
 */
export const isObjectDeclaredAndHasProperties = (obj) => {
  return obj !== null && obj !== undefined && Object.keys(obj).length > 0;
};
